import React, { useEffect, useState, useCallback, useRef } from 'react';
import { fetchPermissions, savePermissions, fetchTenantsList } from '../services/api';
import { useAuth } from '../context/AuthContext';
import type { RolePermissions, PermissionAction, PermissionModule, Tenant } from '../types';

const MODULES: { key: PermissionModule; label: string; hint?: string }[] = [
  { key: 'leads',         label: 'Leads' },
  { key: 'deals',         label: 'Deals' },
  { key: 'contacts',      label: 'Contacts' },
  { key: 'organizations', label: 'Organizations' },
  { key: 'tasks',         label: 'Tasks' },
  { key: 'sim_calls',     label: 'SIM Calls' },
  { key: 'devices',       label: 'Devices' },
  { key: 'campaigns',     label: 'Campaigns' },
  { key: 'assignment',    label: 'Assignment', hint: 'View = check-in/out page. Edit = assign leads & mark attendance.' },
];

const ACTIONS: { key: PermissionAction; label: string; color: string }[] = [
  { key: 'view',   label: 'View',   color: '#0369a1' },
  { key: 'create', label: 'Create', color: '#166534' },
  { key: 'edit',   label: 'Edit',   color: '#92400e' },
  { key: 'delete', label: 'Delete', color: '#991b1b' },
];

// Kept in sync with backend RolePermission::$moduleDefaults. external_user
// gets nothing by default except leads:view — a per-module override below.
const MODULE_DEFAULTS: Partial<Record<PermissionModule, { manager: Record<string,boolean>; marketing: Record<string,boolean>; agent: Record<string,boolean>; external_user: Record<string,boolean> }>> = {
  leads: {
    manager:       { view: true,  create: true,  edit: true,  delete: false },
    marketing:     { view: true,  create: true,  edit: true,  delete: false },
    agent:         { view: true,  create: true,  edit: true,  delete: false },
    external_user: { view: true,  create: false, edit: false, delete: false },
  },
  assignment: {
    manager:       { view: true,  create: true,  edit: true,  delete: false },
    marketing:     { view: true,  create: true,  edit: true,  delete: false },
    agent:         { view: true,  create: false, edit: false, delete: false },
    external_user: { view: false, create: false, edit: false, delete: false },
  },
};

const DEFAULT_PERMS: RolePermissions = {
  manager:       Object.fromEntries(MODULES.map(m => [m.key, MODULE_DEFAULTS[m.key]?.manager       ?? { view: true,  create: true,  edit: true,  delete: false }])) as any,
  marketing:     Object.fromEntries(MODULES.map(m => [m.key, MODULE_DEFAULTS[m.key]?.marketing     ?? { view: true,  create: true,  edit: true,  delete: false }])) as any,
  agent:         Object.fromEntries(MODULES.map(m => [m.key, MODULE_DEFAULTS[m.key]?.agent         ?? { view: true,  create: true,  edit: true,  delete: false }])) as any,
  external_user: Object.fromEntries(MODULES.map(m => [m.key, MODULE_DEFAULTS[m.key]?.external_user ?? { view: false, create: false, edit: false, delete: false }])) as any,
};

type Role = 'manager' | 'marketing' | 'agent' | 'external_user';
const ROLE_LABELS: Record<Role, string> = {
  manager: 'Manager', marketing: 'Marketing', agent: 'Agent', external_user: 'External User',
};

// ── Toggle switch ────────────────────────────────────────────────────────────

const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }> = ({ checked, onChange, disabled }) => (
  <button
    onClick={() => !disabled && onChange(!checked)}
    style={{
      width: 40, height: 22, borderRadius: 11, border: 'none',
      background: checked ? '#7c3aed' : '#e2e8f0',
      position: 'relative', cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'background 0.2s', flexShrink: 0,
      opacity: disabled ? 0.5 : 1,
    }}
  >
    <span style={{
      position: 'absolute', top: 3,
      left: checked ? 21 : 3,
      width: 16, height: 16, borderRadius: '50%',
      background: '#fff', transition: 'left 0.2s',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    }} />
  </button>
);

// ── Main Page ────────────────────────────────────────────────────────────────

const PermissionsPage: React.FC = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  // Tenant selector state (super_admin only)
  const [tenants, setTenants]                     = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId]   = useState<number | null>(null);
  const [tenantsLoading, setTenantsLoading]        = useState(false);

  // Permissions state
  const [perms, setPerms]     = useState<RolePermissions>(DEFAULT_PERMS);
  const [activeRole, setRole] = useState<Role>('manager');
  const [loading, setLoading] = useState(!isSuperAdmin);

  // Save status
  const [saving, setSaving]   = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [error, setError]     = useState('');

  // Debounce timer ref
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Keep latest tenantId ref so debounce callback always has current value
  const tenantIdRef = useRef<number | null>(null);
  tenantIdRef.current = selectedTenantId;

  // Load tenant list for super_admin
  useEffect(() => {
    if (!isSuperAdmin) return;
    setTenantsLoading(true);
    fetchTenantsList()
      .then((data: Tenant[]) => {
        const active = data.filter(t => t.is_active);
        setTenants(active);
        if (active.length > 0) setSelectedTenantId(active[0].id);
      })
      .catch(() => setError('Failed to load tenants'))
      .finally(() => setTenantsLoading(false));
  }, [isSuperAdmin]);

  // Load permissions whenever tenant context changes
  const loadPerms = useCallback((tenantId?: number) => {
    setLoading(true);
    setError('');
    setSaveStatus('idle');
    setPerms(DEFAULT_PERMS);
    fetchPermissions(tenantId)
      .then(data => {
        if (data && Object.keys(data).length > 0) setPerms(data);
      })
      .catch(() => setError('Failed to load permissions'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      if (selectedTenantId) loadPerms(selectedTenantId);
    } else {
      loadPerms(undefined);
    }
  }, [isSuperAdmin, selectedTenantId, loadPerms]);

  // Core save function (takes explicit perms snapshot so debounce always has fresh data)
  const doSave = useCallback(async (snapshot: RolePermissions, tenantId: number | null) => {
    if (isSuperAdmin && !tenantId) return;
    setSaving(true);
    setSaveStatus('saving');
    setError('');
    try {
      const tenantParam = isSuperAdmin ? (tenantId ?? undefined) : undefined;
      const result = await savePermissions(snapshot, tenantParam);
      if (result && Object.keys(result).length > 0) setPerms(result);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2500);
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Failed to save permissions';
      setError(msg);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  }, [isSuperAdmin]);

  // Schedule a debounced auto-save whenever perms change via toggle
  const scheduleAutoSave = useCallback((newPerms: RolePermissions) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSaveStatus('saving'); // show "Saving…" immediately so user sees feedback
    debounceRef.current = setTimeout(() => {
      doSave(newPerms, tenantIdRef.current);
    }, 600);
  }, [doSave]);

  // Toggle a single cell
  const toggle = useCallback((role: Role, module: PermissionModule, action: PermissionAction, value: boolean) => {
    setPerms(prev => {
      const next: RolePermissions = {
        ...prev,
        [role]: { ...prev[role], [module]: { ...prev[role][module], [action]: value } },
      };
      scheduleAutoSave(next);
      return next;
    });
  }, [scheduleAutoSave]);

  // Toggle all modules for a role + action
  const toggleAll = useCallback((role: Role, action: PermissionAction, value: boolean) => {
    setPerms(prev => {
      const next: RolePermissions = {
        ...prev,
        [role]: Object.fromEntries(
          MODULES.map(m => [m.key, { ...prev[role][m.key], [action]: value }])
        ) as any,
      };
      scheduleAutoSave(next);
      return next;
    });
  }, [scheduleAutoSave]);

  // Manual save (explicit button click)
  const handleSave = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    doSave(perms, tenantIdRef.current);
  };

  const selectedTenant = tenants.find(t => t.id === selectedTenantId);
  const rolePerms = perms[activeRole] ?? DEFAULT_PERMS[activeRole];
  const canInteract = !loading && (!isSuperAdmin || !!selectedTenantId);

  const StatusChip = () => {
    if (saveStatus === 'saving') return (
      <span style={{ fontSize: 12, color: '#7c3aed', display: 'flex', alignItems: 'center', gap: 5 }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
        Saving…
      </span>
    );
    if (saveStatus === 'saved') return (
      <span style={{ fontSize: 12, color: '#16a34a', display: 'flex', alignItems: 'center', gap: 5 }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
        Saved
      </span>
    );
    if (saveStatus === 'error') return (
      <span style={{ fontSize: 12, color: '#dc2626' }}>Save failed — try again</span>
    );
    return null;
  };

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Role Permissions</h1>
          <p style={{ marginTop: 6, color: 'var(--text-muted)', fontSize: 14 }}>
            Toggles auto-save. Tenant Admin and Super Admin always have full access.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <StatusChip />
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={saving || !canInteract}
            style={{ minWidth: 120 }}
          >
            {saving ? 'Saving…' : 'Save Now'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#dc2626', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 14 }}>
          {error}
        </div>
      )}

      {/* Super Admin – Tenant Selector */}
      {isSuperAdmin && (
        <div style={{ marginBottom: 20, padding: '16px 20px', background: '#f8f7ff', border: '1px solid #ede9fe', borderRadius: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#7c3aed" strokeWidth={2}>
                <rect x="2" y="3" width="20" height="14" rx="2"/>
                <path d="M8 21h8M12 17v4"/>
              </svg>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#7c3aed' }}>Editing permissions for tenant:</span>
            </div>
            {tenantsLoading ? (
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading tenants…</span>
            ) : (
              <select
                value={selectedTenantId ?? ''}
                onChange={e => {
                  setSaveStatus('idle');
                  setSelectedTenantId(Number(e.target.value) || null);
                }}
                style={{
                  padding: '6px 12px', borderRadius: 7, border: '1.5px solid #c4b5fd',
                  fontSize: 14, fontWeight: 600, color: 'var(--navy)',
                  background: '#fff', cursor: 'pointer', minWidth: 240,
                }}
              >
                {tenants.length === 0 && <option value="">No active tenants</option>}
                {tenants.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.slug})
                  </option>
                ))}
              </select>
            )}
            {selectedTenant && (
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                Tenant ID #{selectedTenant.id}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Role tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {(['manager', 'marketing', 'agent', 'external_user'] as Role[]).map(r => (
          <button
            key={r}
            onClick={() => setRole(r)}
            style={{
              padding: '8px 22px', borderRadius: 8, fontSize: 14, fontWeight: 600,
              border: '2px solid',
              borderColor: activeRole === r ? '#7c3aed' : 'var(--border)',
              background: activeRole === r ? '#ede9fe' : '#fff',
              color: activeRole === r ? '#7c3aed' : 'var(--text-muted)',
              cursor: 'pointer',
            }}
          >
            {ROLE_LABELS[r]}
          </button>
        ))}
      </div>

      {/* Permissions table */}
      {isSuperAdmin && !selectedTenantId ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)', fontSize: 14 }}>
          Select a tenant above to view and edit their permissions.
        </div>
      ) : loading ? (
        <div className="spinner">Loading…</div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', width: 180 }}>
                  Module
                </th>
                {ACTIONS.map(a => (
                  <th key={a.key} style={{ padding: '12px 16px', textAlign: 'center', fontSize: 12, fontWeight: 600, color: a.color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    <div>{a.label}</div>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginTop: 6 }}>
                      <button
                        onClick={() => toggleAll(activeRole, a.key, true)}
                        disabled={!canInteract || saving}
                        style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, border: '1px solid #d1d5db', background: '#f9fafb', cursor: 'pointer', color: '#374151' }}
                      >All</button>
                      <button
                        onClick={() => toggleAll(activeRole, a.key, false)}
                        disabled={!canInteract || saving}
                        style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, border: '1px solid #d1d5db', background: '#f9fafb', cursor: 'pointer', color: '#374151' }}
                      >None</button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MODULES.map((mod, i) => (
                <tr
                  key={mod.key}
                  style={{
                    borderBottom: i < MODULES.length - 1 ? '1px solid var(--border)' : 'none',
                    background: i % 2 === 0 ? '#fff' : '#fafafa',
                  }}
                >
                  <td style={{ padding: '14px 20px' }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--navy)' }}>{mod.label}</div>
                    {mod.hint && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{mod.hint}</div>}
                  </td>
                  {ACTIONS.map(action => {
                    const isOn = rolePerms[mod.key]?.[action.key] ?? false;
                    return (
                      <td key={action.key} style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <Toggle
                            checked={isOn}
                            onChange={v => toggle(activeRole, mod.key, action.key, v)}
                            disabled={!canInteract}
                          />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Note */}
      <div style={{ marginTop: 16, padding: '12px 16px', background: '#f8fafc', borderRadius: 8, border: '1px solid var(--border)', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.8 }}>
        <strong style={{ color: 'var(--text)' }}>Note:</strong> Changes auto-save and take effect on the user's next login.
        Super Admin and Tenant Admin always have full access regardless of these settings.
      </div>
    </div>
  );
};

export default PermissionsPage;
